import {
  Button,
  calendarDay,
  dayRange,
  formatDate,
  formatMoney,
  ListView,
  PageHeader,
  useCursorList,
} from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { FilterToolbar } from "@majitap/ui/arc/filter-toolbar/filter-toolbar";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import {
  INVOICE_SOURCE,
  INVOICE_STATUS,
  type InvoiceSource,
  type InvoiceStatus,
  isOverdue,
  statusBadge,
} from "../../invoices";
import { filterSearch } from "../../list-search";
import { AgeingView } from "./ageing";
import { BatchDialog } from "./batch";
import styles from "./invoices.module.css";
import { TermsView } from "./terms";
import { ToInvoiceView } from "./to-invoice";

const VIEWS = ["invoices", "to-invoice", "ageing", "terms"] as const;
type View = (typeof VIEWS)[number];
/** The segments: Open and Overdue are the API's `standing`, the rest one status each. */
const SHOW = ["all", "draft", "open", "overdue", "paid", "void"] as const;
type Show = (typeof SHOW)[number];
const SHOW_LABEL: Record<Show, string> = {
  all: "All",
  draft: "Drafts",
  open: "Open",
  overdue: "Overdue",
  paid: "Paid",
  void: "Void",
};

export const invoiceFilters = filterSearch({
  view: VIEWS,
  show: SHOW,
  source: Object.keys(INVOICE_SOURCE) as InvoiceSource[],
  // one open stage at a time, beside the segments (which group them as Open)
  stage: ["issued", "sent", "partially_paid"] as const,
  customer_id: "text",
  business_id: "text",
  due_from: "text",
  due_to: "text",
});
type InvoiceSearch = ReturnType<typeof invoiceFilters.validate>;

/** The API query for a segment. */
function showQuery(show: Show | undefined): { status?: InvoiceStatus; standing?: "open" | "overdue" } {
  if (show === "open" || show === "overdue") return { standing: show };
  if (show === "draft" || show === "paid" || show === "void") return { status: show };
  return {};
}

/** Invoices (P8-14, `Console / Invoices` in the design): invoices, who needs invoicing, ageing and payment terms. */
export function InvoicesPage(): ReactNode {
  const search = useSearch({ from: "/console/invoices" });
  const navigate = useNavigate({ from: "/invoices" });
  const view = search.view ?? "invoices";
  const [batch, setBatch] = useState(false);
  return (
    <>
      <PageHeader
        title="Invoices"
        description="Bills to customers and businesses, what they owe, and payment terms."
        actions={
          view === "invoices" ? (
            <>
              <Button variant="secondary" onClick={() => setBatch(true)}>
                From deliveries
              </Button>
              <Button onClick={() => navigate({ to: "/invoices/new" })}>New invoice</Button>
            </>
          ) : undefined
        }
      />
      <SegmentedControl
        label="Show"
        value={view}
        onValueChange={(v) => navigate({ search: { view: v === "invoices" ? undefined : (v as View) } })}
        options={[
          { value: "invoices", label: "Invoices" },
          { value: "to-invoice", label: "To invoice" },
          { value: "ageing", label: "Ageing" },
          { value: "terms", label: "Payment terms" },
        ]}
      />
      {view === "invoices" ? <InvoiceList search={search} /> : null}
      {view === "to-invoice" ? <ToInvoiceView /> : null}
      {view === "ageing" ? <AgeingView /> : null}
      {view === "terms" ? <TermsView /> : null}
      {batch ? <BatchDialog onClose={() => setBatch(false)} /> : null}
    </>
  );
}

function InvoiceList({ search }: { search: InvoiceSearch }) {
  const navigate = useNavigate({ from: "/invoices" });
  const format = useCompanyFormat();
  const now = Date.now();
  const { view: _view, show, stage, due_from, due_to, ...rest } = search;
  const list = useCursorList({
    queryKey: ["invoices", search],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/billing/invoices", {
        params: {
          query: {
            ...rest,
            ...showQuery(show),
            ...(stage ? { status: stage } : {}),
            due_from,
            due_to,
            cursor,
            limit: 25,
          },
        },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const set = (patch: Partial<InvoiceSearch>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const first = list.rows[0];
  const chips = [
    ...(search.source ? [{ id: "source", label: "Source", value: INVOICE_SOURCE[search.source] }] : []),
    ...(stage ? [{ id: "stage", label: "Stage", value: INVOICE_STATUS[stage][0] }] : []),
    ...(search.customer_id ? [{ id: "customer_id", label: "Customer", value: first?.billee.name ?? "chosen" }] : []),
    ...(search.business_id ? [{ id: "business_id", label: "Business", value: first?.billee.name ?? "chosen" }] : []),
  ];

  return (
    <ListView
      caption="Invoices"
      itemName={{ one: "invoice", other: "invoices" }}
      rows={list.rows}
      rowKey={(i) => i.id}
      status={list.status}
      errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
      onRetry={list.refetch}
      hasMore={list.hasMore}
      loadingMore={list.loadingMore}
      onLoadMore={list.loadMore}
      filtered={invoiceFilters.active(search).some(([k]) => k !== "view")}
      onResetFilters={() => navigate({ search: {} })}
      empty={{
        title: "No invoices yet",
        description: "Raise one by hand, from an order, or from a business's deliveries over a period.",
      }}
      toolbar={
        <>
          <SegmentedControl
            label="Status"
            value={show ?? "all"}
            onValueChange={(v) => set({ show: v === "all" ? undefined : (v as Show) })}
            options={SHOW.map((s) => ({ value: s, label: SHOW_LABEL[s] }))}
          />
          <div className={styles.toolbarEnd}>
            <DateRangePicker
              label="Due"
              placeholder="Any due date"
              value={
                due_from && due_to
                  ? {
                      start: calendarDay(due_from, format.timezone),
                      end: calendarDay(new Date(Date.parse(due_to) - 1).toISOString(), format.timezone),
                    }
                  : null
              }
              onChange={(range) => {
                const r = dayRange(range, format.timezone);
                set({ due_from: r.from, due_to: r.to });
              }}
            />
            <FilterToolbar
              filters={chips}
              onRemove={(id) => set({ [id]: undefined })}
              onClearAll={() => navigate({ search: {} })}
              addFilter={{
                fields: [
                  {
                    id: "source",
                    label: "Source",
                    options: (Object.keys(INVOICE_SOURCE) as InvoiceSource[]).map((s) => ({
                      value: s,
                      label: INVOICE_SOURCE[s],
                    })),
                  },
                  {
                    id: "stage",
                    label: "Stage",
                    options: (["issued", "sent", "partially_paid"] as const).map((s) => ({
                      value: s,
                      label: INVOICE_STATUS[s][0],
                    })),
                  },
                ],
                onAdd: (chip, field) =>
                  field.id === "stage"
                    ? set({ stage: chip.value as "issued" | "sent" | "partially_paid", show: undefined })
                    : set({ source: chip.value as InvoiceSource }),
              }}
            />
          </div>
        </>
      }
      columns={[
        {
          key: "invoice_number",
          label: "Invoice",
          render: (i) => (
            <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className={styles.number}>
              {i.invoice_number ?? "Draft"}
            </Link>
          ),
        },
        {
          key: "billee",
          label: "Billed to",
          render: (i) =>
            i.billee.kind === "business" ? (
              <Link to="/businesses/$businessId" params={{ businessId: i.billee.id }} className={styles.plainLink}>
                {i.billee.name}
              </Link>
            ) : (
              <Link to="/customers/$customerId" params={{ customerId: i.billee.id }} className={styles.plainLink}>
                {i.billee.name}
              </Link>
            ),
        },
        {
          key: "source",
          label: "Source",
          render: (i) => <span className={styles.muted}>{INVOICE_SOURCE[i.source]}</span>,
        },
        {
          key: "due_at",
          label: "Due",
          render: (i) =>
            i.due_at ? (
              <span className={isOverdue(i, now) ? styles.overdue : undefined}>{formatDate(i.due_at, format)}</span>
            ) : (
              "—"
            ),
        },
        {
          key: "total_amount",
          label: "Total",
          numeric: true,
          render: (i) => formatMoney(i.total_amount, { ...format, currency: i.currency }),
        },
        {
          key: "amount_due",
          label: "Owed",
          numeric: true,
          render: (i) => formatMoney(i.amount_due, { ...format, currency: i.currency }),
        },
        {
          key: "status",
          label: "Status",
          render: (i) => {
            const [label, tone] = statusBadge(i, now);
            return <Badge tone={tone}>{label}</Badge>;
          },
        },
      ]}
    />
  );
}
